import React from 'react';

const Section4RAG = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-emerald-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">🔍</span>
      <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
        RAG & Semantic Search
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">How the AI knows things, not just API calls</p>

    {/* Embedding stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
      {[
        { icon: '📐', value: '1,536', label: 'Embedding Dimensions' },
        { icon: '🎨', value: '200+', label: 'Theme Embeddings' },
        { icon: '📿', value: '105', label: 'Ritual Embeddings' },
        { icon: '⚡', value: '<200ms', label: 'Search Latency' },
      ].map((stat, i) => (
        <div key={i} className="bg-gradient-to-br from-emerald-50 to-teal-50/50 rounded-2xl p-4 text-center border border-emerald-200/50 hover:scale-105 transition-transform duration-300">
          <span className="text-2xl block mb-1">{stat.icon}</span>
          <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
        </div>
      ))}
    </div>

    {/* Dual-vector architecture — the key engineering decision */}
    <div className="bg-gradient-to-br from-emerald-50/50 to-teal-50/50 rounded-2xl p-6 border border-emerald-200/30 mb-6">
      <h3 className="text-base font-bold text-emerald-600 uppercase tracking-wider mb-5 text-center">Dual-Vector Architecture: Right Tool for the Right Job</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* RAG side */}
        <div className="bg-white/80 rounded-xl p-5 border border-emerald-200/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-800 text-sm">RAG Semantic Search</h4>
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-500 to-teal-500 text-white">Themes & Rituals</span>
          </div>
          <div className="space-y-2 mb-4">
            {[
              'Unstructured text: descriptions, meanings, symbolism',
              'OpenAI text-embedding-3-small (1536 dimensions)',
              'pgvector for vector storage and similarity search',
              'Understands meaning, not just keywords',
              '150-200ms per search',
            ].map((item, i) => (
              <div key={i} className="flex items-start">
                <span className="text-emerald-500 text-xs mr-1.5 mt-0.5">✓</span>
                <span className="text-sm text-gray-600">{item}</span>
              </div>
            ))}
          </div>
          <div className="bg-emerald-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-emerald-700">"symbol of eternal love" → finds rings, handfasting, mangalsutra</p>
          </div>
        </div>

        {/* Cosine similarity side */}
        <div className="bg-white/80 rounded-xl p-5 border border-teal-200/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-800 text-sm">Cosine Similarity Vectors</h4>
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white">Vendors</span>
          </div>
          <div className="space-y-2 mb-4">
            {[
              'AI-profiled during vendor onboarding (one-time)',
              'Structured data: 6 pre-computed dimensions',
              'Style, color, mood, formality, cultural, seasonal',
              'Direct mathematical vector comparison',
              '50ms per match, 3x faster than RAG',
            ].map((item, i) => (
              <div key={i} className="flex items-start">
                <span className="text-teal-500 text-xs mr-1.5 mt-0.5">✓</span>
                <span className="text-sm text-gray-600">{item}</span>
              </div>
            ))}
          </div>
          <div className="bg-teal-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-teal-700">Theme style + Vendor style → compatibility score per dimension → ranked matches</p>
          </div>
        </div>
      </div>
    </div>

    {/* Embedding pipeline */}
    <div className="bg-gradient-to-br from-emerald-50/50 to-teal-50/50 rounded-2xl p-6 border border-emerald-200/30 mb-6">
      <h3 className="text-base font-bold text-emerald-600 uppercase tracking-wider mb-5 text-center">Embedding Pipeline</h3>
      <div className="flex flex-col items-center">
        <div className="flex flex-wrap justify-center gap-3 mb-3">
          {[
            { step: 'Content Created', icon: '💾' },
            { step: 'Text Extracted', icon: '📝' },
            { step: 'Embedding Generated', icon: '🧮' },
            { step: 'Stored in pgvector', icon: '🗄️' },
            { step: 'Available for Search', icon: '🔍' },
          ].map((s, i) => (
            <React.Fragment key={i}>
              <div className="bg-white rounded-xl px-4 py-3 shadow-md border border-emerald-200/50 text-center">
                <span className="text-lg block">{s.icon}</span>
                <p className="text-xs font-bold text-gray-700 mt-1">{s.step}</p>
              </div>
              {i < 4 && <span className="text-emerald-300 font-bold hidden md:flex items-center">→</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          {[
            { label: 'Auto-embed on save', desc: 'Non-blocking. Save succeeds even if embedding fails' },
            { label: 'Batch processing', desc: 'Scripts for bulk embedding with 100ms rate limiting' },
            { label: 'Cost', desc: '$0.02 per 1M tokens, minimal overhead' },
          ].map((item, i) => (
            <div key={i} className="bg-white/60 rounded-lg px-3 py-2 border border-emerald-100/50">
              <span className="text-xs font-bold text-emerald-700">{item.label}</span>
              <span className="text-xs text-gray-400 ml-1">· {item.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Multi-factor ritual matching */}
    <div className="bg-gradient-to-r from-emerald-50/80 to-teal-50/80 rounded-2xl p-5 border border-emerald-200/30">
      <h3 className="text-base font-bold text-emerald-600 uppercase tracking-wider mb-3">🎯 Multi-Factor Ritual Matching Algorithm</h3>
      <p className="text-sm text-gray-500 mb-4">Theme-to-ritual compatibility uses 5 weighted factors, not just semantic similarity</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { factor: 'Semantic Similarity', weight: '40%', desc: 'How closely the ritual\'s meaning matches the theme', color: 'emerald' },
          { factor: 'Style Match', weight: '25%', desc: 'Overlap between ritual themes and theme style keywords', color: 'teal' },
          { factor: 'Formality Match', weight: '15%', desc: 'Casual ritual + formal theme = lower score', color: 'green' },
          { factor: 'Cultural Relevance', weight: '10%', desc: 'Boost if user\'s saved themes show cultural preference', color: 'cyan' },
          { factor: 'Symbolism Match', weight: '10%', desc: 'Shared symbols like unity, eternity, commitment', color: 'emerald' },
        ].map((f, i) => (
          <div key={i} className={`bg-white/80 rounded-xl p-3 border border-${f.color}-200/50 text-center`}>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-${f.color}-500 to-${f.color}-600 text-white mb-2`}>{f.weight}</span>
            <p className="text-sm font-bold text-gray-800">{f.factor}</p>
            <p className="text-xs text-gray-400 mt-1">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>

);

export default Section4RAG;
